import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, EyeOff, ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { EmptyState, fmtDate } from "@/components/PageHeader";
import { useFeatureFlags, FeatureDisabled } from "@/lib/featureFlags";

function fmtDeadline(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function ReadOption({ option, active }) {
  return (
    <div className={`flex w-full items-center gap-3 rounded-lg border p-3 text-sm ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600"}`}
      data-testid={`parent-choice-option-${option.id}${active ? "-selected" : ""}`}>
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${active ? "border-white" : "border-slate-300"}`}>
        {active && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="font-medium">{option.label}</span>
    </div>
  );
}

function ReadChoiceCard({ choice, collapsible = false }) {
  const sel = choice.my_option_ids || [];
  const isMenu = (choice.categories || []).length > 0;
  const [expanded, setExpanded] = useState(!collapsible);
  return (
    <Card className="border-slate-200 p-4 shadow-sm" data-testid={`parent-choice-${choice.id}`}>
      <div className={`flex items-start justify-between gap-2 ${collapsible ? "cursor-pointer select-none" : ""}`}
        onClick={collapsible ? () => setExpanded((v) => !v) : undefined}
        data-testid={collapsible ? `parent-choice-toggle-${choice.id}` : undefined}>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">{choice.title}</p>
          {expanded && choice.description && <p className="mt-0.5 text-sm text-slate-600">{choice.description}</p>}
          {collapsible && !expanded && (
            <p className="mt-0.5 text-xs text-slate-400">{choice.responded ? "Your student responded" : "No response"} · {fmtDate(choice.created_at)}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-xs ${choice.accepting ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{choice.accepting ? "Open" : "Closed"}</span>
          {collapsible && <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />}
        </div>
      </div>
      {expanded && (<>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
        <span className={`font-semibold ${choice.responded ? "text-emerald-600" : "text-amber-600"}`}>
          {choice.responded ? "Your student responded" : "No response yet"}
        </span>
        {choice.anonymous && <span className="inline-flex items-center gap-1 text-amber-600"><EyeOff className="h-3 w-3" /> Anonymous</span>}
        {choice.deadline && <span>· Closes {fmtDeadline(choice.deadline)}</span>}
        <span>· {fmtDate(choice.created_at)}</span>
      </div>

      {isMenu ? (
        <div className="mt-3 space-y-4">
          {choice.categories.map((cat) => (
            <div key={cat.id}>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">{cat.name}</p>
              <div className="space-y-2">
                {cat.options.map((o) => <ReadOption key={o.id} option={o} active={sel.includes(o.id)} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {choice.options.map((o) => <ReadOption key={o.id} option={o} active={sel.includes(o.id)} />)}
        </div>
      )}
      </>)}
    </Card>
  );
}

export default function ParentStudentChoices() {
  const { choices: choicesOn } = useFeatureFlags();
  const { id } = useParams();
  const [items, setItems] = useState(null);

  useEffect(() => { api.get(`/parent/students/${id}/choices`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([])); }, [id]);

  if (!choicesOn) return <FeatureDisabled name="Choices" />;
  if (items === null) return <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const open = items.filter((c) => c.accepting);
  const closed = items.filter((c) => !c.accepting);

  return (
    <div data-testid="parent-student-choices">
      <p className="mb-4 text-sm text-slate-500">Prompts and polls your student's coach has asked them to respond to. View only.</p>
      {items.length === 0 ? (
        <EmptyState title="No choices yet" description="No prompts have been posted for this student's team." testId="parent-choices-empty" />
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Open prompts</h3>
            {open.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-400" data-testid="parent-choices-no-open">No open prompts right now.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2" data-testid="parent-choices-open">
                {open.map((c) => <ReadChoiceCard key={c.id} choice={c} />)}
              </div>
            )}
          </div>
          {closed.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Ended prompts</h3>
              <div className="space-y-2" data-testid="parent-choices-closed">
                {closed.map((c) => <ReadChoiceCard key={c.id} choice={c} collapsible />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
